import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { IntakeRenderer, getMissingRequired } from '@helios/ui/intake';
import type { IntakeQuestionnaire, IntakeAnswers, Option, FieldRenderer } from '@helios/ui/intake';
import {
  ALL_COUNTRIES,
  CountryPicker,
  buildCountryOptions,
  getMobileCountryPickerConfig,
  iso2ToDialCode,
} from '@helios/ui/country-picker';
import { PATIENT_CORE_FIELDS } from '@helios/domain';
import {
  qPatientFieldDefinitionOverrides,
  buildPatientFormSettingsVM,
  buildPatientValidationSchema,
  qLatestPublishedIntakeFormDefinition,
  qAppConfig,
  getPatientFormLayoutMode,
  type PatientFieldDefinitionRow,
  type EffectivePatientField,
  type IntakeFormDefinitionRow,
  type AppConfigRow,
} from '@helios/data-access';
import { parseValidationSchema, validateAnswers } from '@helios/validation';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import { useEnabledIdTypeLabels } from '../hooks/useRegionalIdTypes';
import { Modal } from '@helios/ui/overlay';
import { ChevronIcon, NavIcon, orgName, useBrandTokens } from '../shell/shared';
import publishedQuestionnaire from './patient/fixtures/intake-questionnaire.published.json';
import {
  buildStepQuestionnaire,
  buildMultiSectionQuestionnaire,
  type FieldOptionsMap,
  type SectionConfig,
} from './patient/buildStepQuestionnaire';

// ─── Step definitions ───

const STEPS = [
  { number: 1, label: 'Personal Info' },
  { number: 2, label: 'Contact' },
  { number: 3, label: 'Emergency Contact' },
  { number: 4, label: 'Medication' },
  { number: 5, label: 'Intake Form' },
] as const;

// ─── Advanced validation helper ───

function hasAdvancedErrors(
  questionnaire: IntakeQuestionnaire,
  schema: string | null,
  answers: IntakeAnswers,
): boolean {
  if (!schema) return false;
  const parsed = parseValidationSchema(schema);
  if (!parsed) return false;
  return validateAnswers(questionnaire, parsed, answers).length > 0;
}

// ─── Form state ───

interface EmergencyContact {
  searchQuery: string;
  name: string;
  relationship: string;
  phone: string;
  email: string;
}

interface FormState {
  // Step 1: Personal Info
  location: string;
  doctor: string;
  patientId: string;
  title: string;
  patientName: string;
  idType: string;
  idNumber: string;
  localName: string;
  countryOfBirth: string;
  gender: string;
  maritalStatus: string;
  dateOfBirth: string;
  shift: string;
  nationality: string;
  race: string;
  literacyLevel: string;
  religion: string;
  occupation: string;
  referredBy: string;
  departmentName: string;
  jobPosition: string;
  status: string;
  // Step 2: Contact
  email: string;
  mobileCountryCode: string;
  mobileNumber: string;
  // Formal Address
  formalTelephone: string;
  formalPostalCode: string;
  formalUnit: string;
  formalResidentialAddress: string;
  formalResidentialAddressLocal: string;
  formalProvinceState: string;
  formalDistrict: string;
  formalSubDistrict: string;
  // Present Address
  // sameAsFormal: string; // we do not need to support this
  presentTelephone: string;
  presentPostalCode: string;
  presentUnit: string;
  presentAddress: string;
  presentAddressLocal: string;
  presentProvinceState: string;
  presentDistrict: string;
  presentSubDistrict: string;
  // Office
  officeTelephone: string;
  officeAddress: string;
  // Step 3: Emergency Contact
  emergencyContacts: EmergencyContact[];
  // Step 4: Medication
  drugAllergies: string;
  foodAllergies: string;
  drugAllergiesSpecify: string;
  drugAllergySymptoms: string;
  drugAllergySeverity: string;
  foodAllergiesSpecify: string;
  foodAllergySymptoms: string;
  foodAllergySeverity: string;
  aboBloodGroup: string;
  rhAntigen: string;
  notes: string;
  alerts: string;
  pastMedication: string;
  currentMedication: string;
  medicalHistory: string;
}

const INITIAL_FORM_STATE: FormState = {
  location: 'Main Clinic',
  doctor: '',
  patientId: '',
  title: '',
  patientName: '',
  idType: '',
  idNumber: '',
  localName: '',
  countryOfBirth: '',
  gender: '',
  maritalStatus: '',
  dateOfBirth: '',
  shift: '',
  nationality: '',
  race: '',
  literacyLevel: '',
  religion: '',
  occupation: '',
  referredBy: '',
  departmentName: '',
  jobPosition: '',
  status: '',
  email: '',
  mobileCountryCode: '',
  mobileNumber: '',
  formalTelephone: '',
  formalPostalCode: '',
  formalUnit: '',
  formalResidentialAddress: '',
  formalResidentialAddressLocal: '',
  formalProvinceState: '',
  formalDistrict: '',
  formalSubDistrict: '',
  presentTelephone: '',
  presentPostalCode: '',
  presentUnit: '',
  presentAddress: '',
  presentAddressLocal: '',
  presentProvinceState: '',
  presentDistrict: '',
  presentSubDistrict: '',
  officeTelephone: '',
  officeAddress: '',
  emergencyContacts: [{ searchQuery: '', name: '', relationship: '', phone: '', email: '' }],
  drugAllergies: '',
  foodAllergies: '',
  drugAllergiesSpecify: '',
  drugAllergySymptoms: '',
  drugAllergySeverity: '',
  foodAllergiesSpecify: '',
  foodAllergySymptoms: '',
  foodAllergySeverity: '',
  aboBloodGroup: '',
  rhAntigen: '',
  notes: '',
  alerts: '',
  pastMedication: '',
  currentMedication: '',
  medicalHistory: '',
};

const EMPTY_CONTACT: EmergencyContact = {
  searchQuery: '',
  name: '',
  relationship: '',
  phone: '',
  email: '',
};

// ─── Save helpers ───

function safeTrim(v: string): string | null {
  const t = v.trim();
  return t || null;
}

function toIsoDateOnly(v: string): string | null {
  if (!v) return null;
  // Accept YYYY-MM-DD or attempt Date parse
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** Build column→value entries for the patients INSERT. Only includes non-null values. */
function buildPatientInsertFromForm(id: string, fs: FormState) {
  const cols: Record<string, string> = { id };

  const map: [string, string | null][] = [
    ['patient_number', safeTrim(fs.patientId)],
    ['full_name', safeTrim(fs.patientName)],
    ['birth_date', toIsoDateOnly(fs.dateOfBirth)],
    ['sex', safeTrim(fs.gender)],
    ['status', safeTrim(fs.status)],
    ['title', safeTrim(fs.title)],
    ['local_name', safeTrim(fs.localName)],
    ['country_of_birth', safeTrim(fs.countryOfBirth)],
    ['marital_status', safeTrim(fs.maritalStatus)],
    ['shift', safeTrim(fs.shift)],
    ['nationality', safeTrim(fs.nationality)],
    ['race', safeTrim(fs.race)],
    ['literacy_level', safeTrim(fs.literacyLevel)],
    ['religion', safeTrim(fs.religion)],
    ['occupation', safeTrim(fs.occupation)],
    ['referred_by', safeTrim(fs.referredBy)],
    ['department_name', safeTrim(fs.departmentName)],
    ['job_position', safeTrim(fs.jobPosition)],
    ['assigned_doctor', safeTrim(fs.doctor)],
    ['location', safeTrim(fs.location)],
    // Medical summary (Step 4)
    ['blood_group_abo', safeTrim(fs.aboBloodGroup)],
    ['blood_group_rh', safeTrim(fs.rhAntigen)],
    ['drug_allergies_notes', safeTrim(fs.drugAllergies)],
    ['food_allergies_notes', safeTrim(fs.foodAllergies)],
    ['medical_notes', safeTrim(fs.notes)],
    ['medical_alerts', safeTrim(fs.alerts)],
    ['past_medication_notes', safeTrim(fs.pastMedication)],
    ['current_medication_notes', safeTrim(fs.currentMedication)],
    ['medical_history', safeTrim(fs.medicalHistory)],
  ];

  for (const [col, val] of map) {
    if (val !== null) cols[col] = val;
  }

  return cols;
}

/** Build column→value entries for the patient_contacts INSERT. Always includes id + patient_id. */
function buildPatientContactsInsertFromForm(id: string, patientId: string, fs: FormState) {
  const cols: Record<string, string> = { id, patient_id: patientId };

  const map: [string, string | null][] = [
    // Contact
    ['email', safeTrim(fs.email)],
    ['mobile_country_code', fs.mobileCountryCode ? iso2ToDialCode(fs.mobileCountryCode) : null],
    ['mobile_number', safeTrim(fs.mobileNumber)],
    // Formal address
    ['formal_telephone', safeTrim(fs.formalTelephone)],
    ['formal_postal_code', safeTrim(fs.formalPostalCode)],
    ['formal_unit', safeTrim(fs.formalUnit)],
    ['formal_residential_address', safeTrim(fs.formalResidentialAddress)],
    ['formal_residential_address_local', safeTrim(fs.formalResidentialAddressLocal)],
    ['formal_province_state', safeTrim(fs.formalProvinceState)],
    ['formal_district', safeTrim(fs.formalDistrict)],
    ['formal_sub_district', safeTrim(fs.formalSubDistrict)],
    // Present address
    ['present_telephone', safeTrim(fs.presentTelephone)],
    ['present_postal_code', safeTrim(fs.presentPostalCode)],
    ['present_unit', safeTrim(fs.presentUnit)],
    ['present_address', safeTrim(fs.presentAddress)],
    ['present_address_local', safeTrim(fs.presentAddressLocal)],
    ['present_province_state', safeTrim(fs.presentProvinceState)],
    ['present_district', safeTrim(fs.presentDistrict)],
    ['present_sub_district', safeTrim(fs.presentSubDistrict)],
    // Office
    ['office_telephone', safeTrim(fs.officeTelephone)],
    ['office_address', safeTrim(fs.officeAddress)],
  ];

  for (const [col, val] of map) {
    if (val !== null) cols[col] = val;
  }

  return cols;
}

/** Build 0–2 allergy insert objects from form state. */
function buildPatientAllergyInsertsFromForm(
  fs: FormState,
  patientId: string,
  genId: () => string,
): Record<string, string>[] {
  const rows: Record<string, string>[] = [];

  const buildRow = (
    type: string,
    selection: string,
    specify: string,
    symptoms: string,
    severity: string,
  ) => {
    const sel = selection.trim();
    if (!sel || sel === 'No Known Allergies') return;

    const substance = sel === 'Yes' ? specify.trim() || 'Unspecified' : sel;

    const cols: Record<string, string> = {
      id: genId(),
      patient_id: patientId,
      allergy_type: type,
      substance,
    };

    const reaction = symptoms.trim();
    if (reaction) cols.reaction = reaction;

    const sev = severity.trim();
    if (sev) cols.severity = sev;

    rows.push(cols);
  };

  buildRow(
    'drug',
    fs.drugAllergies,
    fs.drugAllergiesSpecify,
    fs.drugAllergySymptoms,
    fs.drugAllergySeverity,
  );
  buildRow(
    'food',
    fs.foodAllergies,
    fs.foodAllergiesSpecify,
    fs.foodAllergySymptoms,
    fs.foodAllergySeverity,
  );

  return rows;
}

// ─── Validation ───

function getStepErrors(step: number, formState: FormState): string[] {
  const errors: string[] = [];
  switch (step) {
    case 1:
      if (!formState.patientId.trim()) errors.push('patientId');
      if (!formState.patientName.trim()) errors.push('patientName');
      break;
    case 2:
      // All contact fields optional per DB schema
      break;
    case 3:
      // Emergency contact name not strictly required on creation
      break;
    case 4:
      // All medication fields optional
      break;
    // Step 5 handled separately via IntakeRenderer
  }
  return errors;
}

// ─── Step 1 select options (driven by IntakeRenderer) ───

function opt(value: string): Option {
  return { id: value, label: value, value };
}

const STEP1_OPTIONS: FieldOptionsMap = {
  doctor: ['Dr. Smith', 'Dr. Johnson', 'Dr. Williams'].map(opt),
  title: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'].map(opt),
  idType: ['National ID', 'Passport', 'Driver License', 'SSN', 'Other'].map(opt),
  gender: ['Male', 'Female', 'Non-binary', 'Prefer not to say'].map(opt),
  maritalStatus: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'].map(opt),
  referredBy: ['Walk-in', 'Doctor Referral', 'Online', 'Insurance'].map(opt),
  status: ['Active', 'Inactive', 'Archived'].map(opt),
};

// ─── Step 4 select options & section layout ───

const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'].map(opt);

const STEP4_OPTIONS: FieldOptionsMap = {
  drugAllergies: ['No Known Allergies', 'Yes'].map(opt),
  foodAllergies: ['No Known Allergies', 'Yes'].map(opt),
  drugAllergySeverity: SEVERITY_OPTIONS,
  foodAllergySeverity: SEVERITY_OPTIONS,
  aboBloodGroup: ['A', 'B', 'AB', 'O'].map(opt),
  rhAntigen: ['Positive', 'Negative'].map(opt),
};

const STEP4_SECTIONS: SectionConfig[] = [
  {
    id: 'section-drug-allergies',
    title: 'Drug Allergies',
    fieldKeys: [
      'drugAllergies',
      'drugAllergiesSpecify',
      'drugAllergySymptoms',
      'drugAllergySeverity',
    ],
  },
  {
    id: 'section-food-allergies',
    title: 'Food Allergies',
    fieldKeys: [
      'foodAllergies',
      'foodAllergiesSpecify',
      'foodAllergySymptoms',
      'foodAllergySeverity',
    ],
  },
  {
    id: 'section-blood-type',
    title: 'Blood Type',
    fieldKeys: ['aboBloodGroup', 'rhAntigen'],
  },
  {
    id: 'section-notes-alerts',
    title: 'Notes & Alerts',
    fieldKeys: ['notes', 'alerts'],
  },
  {
    id: 'section-medication-history',
    title: 'Medication History',
    fieldKeys: ['pastMedication', 'currentMedication', 'medicalHistory'],
  },
];

// ─── Page Header (97px) ───

function PageHeader({
  title,
  subtitle,
  versionPill,
  createDisabled,
  onCreatePatient,
  saving,
  saveError,
  createdPatientId,
}: {
  title?: string;
  subtitle?: string;
  versionPill?: number;
  createDisabled?: boolean;
  onCreatePatient?: () => void;
  saving?: boolean;
  saveError?: string | null;
  createdPatientId?: string | null;
}) {
  return (
    <div
      style={{
        height: 'var(--clinic-header-height)',
        padding: '20px var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexShrink: 0,
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              letterSpacing: '0.07px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {title ?? 'Create Patient'}
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            {subtitle ?? `Add a new patient record to ${orgName}`}
          </p>
        </div>
        {versionPill !== undefined && (
          <span
            style={{
              height: 24,
              padding: '0 10px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: '24px',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
            }}
          >
            Version {versionPill}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}>
        <Button
          variant="outline"
          size="md"
          onClick={() => {
            console.log('[Save Draft] form state saved');
          }}
        >
          Save Draft
        </Button>
        <Button
          variant="gradient"
          size="md"
          disabled={createDisabled || saving || !!createdPatientId}
          onClick={() => onCreatePatient?.()}
          style={createDisabled || saving ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {saving ? 'Creating\u2026' : createdPatientId ? 'Created' : 'Create Patient'}
        </Button>
      </div>

      {/* Save status feedback */}
      {(saveError || createdPatientId) && (
        <div
          style={{
            position: 'absolute',
            right: 'var(--sp-6)',
            top: 'calc(var(--clinic-header-height) - 24px)',
            fontSize: 'var(--font-size-sm)',
            color: saveError ? 'var(--color-danger-text)' : 'var(--color-success-text)',
          }}
        >
          {saveError ?? `Patient created (ID: ${createdPatientId?.slice(0, 8)}\u2026)`}
        </div>
      )}
    </div>
  );
}

// ─── Form Stepper ───

function FormStepper({
  currentStep,
  maxStepReached,
  onStepClick,
  singlePage,
  validSteps,
}: {
  currentStep: number;
  maxStepReached: number;
  onStepClick: (step: number) => void;
  singlePage?: boolean;
  validSteps?: Set<number>;
}) {
  return (
    <div
      style={{
        width: 'var(--clinic-stepper-width)',
        padding: 'var(--sp-6) 0 var(--sp-6) var(--sp-6)',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        {STEPS.map((step) => {
          const isActive = step.number === currentStep;
          const isCompleted = singlePage
            ? validSteps?.has(step.number) === true && step.number !== currentStep
            : step.number < currentStep;
          const canClick = step.number <= maxStepReached && step.number !== currentStep;

          return (
            <div
              key={step.number}
              onClick={canClick ? () => onStepClick(step.number) : undefined}
              style={{
                width: 231,
                height: isActive ? 52 : 48,
                borderRadius: isActive ? 'var(--radius-lg)' : 'var(--radius-md)',
                background: isActive ? 'var(--gradient-nav-active)' : 'transparent',
                border: isActive ? '2px solid var(--border-strong)' : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                padding: isActive ? '0 18px' : '0 16px',
                gap: 'var(--sp-3)',
                cursor: canClick ? 'pointer' : isActive ? 'default' : 'not-allowed',
                opacity: !isActive && !isCompleted && step.number > maxStepReached ? 0.5 : 1,
                transition: 'opacity 200ms',
              }}
            >
              {/* Step number circle */}
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--radius-full)',
                  background: isActive
                    ? 'var(--color-primary)'
                    : isCompleted
                      ? 'var(--color-success)'
                      : 'var(--bg-surface-2)',
                  color: isActive || isCompleted ? 'var(--text-on-primary)' : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  flexShrink: 0,
                }}
              >
                {isCompleted ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'block' }}
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                ) : (
                  step.number
                )}
              </div>

              {/* Step label */}
              <span
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: 'var(--line-height-base)',
                  letterSpacing: '-0.15px',
                  color: isActive ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Emergency Contact Step ───

const RELATIONSHIP_OPTIONS = [
  '',
  'Spouse',
  'Parent',
  'Child',
  'Sibling',
  'Relative',
  'Friend',
  'Other',
];

// Map from field_key to EmergencyContact property
const EC_FIELD_MAP: Record<string, keyof EmergencyContact> = {
  emergencyContactName: 'name',
  emergencyContactRelationship: 'relationship',
  emergencyContactPhone: 'phone',
  emergencyContactEmail: 'email',
};

function NextOfKinCard({
  index,
  contact,
  onContactChange,
  onRemove,
  canRemove,
  ecFields,
}: {
  index: number;
  contact: EmergencyContact;
  onContactChange: (field: keyof EmergencyContact, value: string) => void;
  onRemove?: () => void;
  canRemove: boolean;
  ecFields: EffectivePatientField[];
}) {
  // Build lookup: EmergencyContact key → effective field
  const fieldFor = (prop: keyof EmergencyContact) =>
    ecFields.find((f) => EC_FIELD_MAP[f.field_key] === prop);
  const inputStyle = {
    height: 'var(--input-height)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--input-radius)',
    padding: '0 var(--input-padding-x)',
    fontSize: 'var(--font-size-base)',
    color: 'var(--input-text)',
    background: 'var(--input-bg)',
    width: '100%',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 'var(--line-height-base)',
    color: 'var(--text)',
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 16,
        background: '#fafbfc',
        padding: 25,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-base)',
              letterSpacing: '-0.15px',
              color: 'var(--text)',
              flexShrink: 0,
            }}
          >
            Next of Kin {index}
          </span>
          {canRemove && onRemove && (
            <button
              onClick={onRemove}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
              }}
              title="Remove"
            >
              <NavIcon name="close" />
            </button>
          )}
        </div>
      </div>

      {/* Next of Kin Name */}
      {(fieldFor('name')?.visible ?? true) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label style={labelStyle}>
            {fieldFor('name')?.label ?? 'Next of Kin Name'}
            {fieldFor('name')?.required && (
              <span style={{ color: 'var(--color-danger-text)' }}> *</span>
            )}
          </label>
          <input
            type="text"
            value={contact.name}
            onChange={(e) => onContactChange('name', e.target.value)}
            placeholder="Enter name"
            style={inputStyle}
          />
        </div>
      )}

      {/* Relationship + Phone */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-4)',
        }}
      >
        {(fieldFor('relationship')?.visible ?? true) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <label style={labelStyle}>
              {fieldFor('relationship')?.label ?? 'Relationship'}
              {fieldFor('relationship')?.required && (
                <span style={{ color: 'var(--color-danger-text)' }}> *</span>
              )}
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={contact.relationship}
                onChange={(e) => onContactChange('relationship', e.target.value)}
                style={{
                  ...inputStyle,
                  appearance: 'none',
                  cursor: 'pointer',
                  paddingRight: 40,
                }}
              >
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || 'Select...'}
                  </option>
                ))}
              </select>
              <div
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'var(--text-muted)',
                }}
              >
                <NavIcon name="chevron-down" />
              </div>
            </div>
          </div>
        )}
        {(fieldFor('phone')?.visible ?? true) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <label style={labelStyle}>
              {fieldFor('phone')?.label ?? 'Phone'}
              {fieldFor('phone')?.required && (
                <span style={{ color: 'var(--color-danger-text)' }}> *</span>
              )}
            </label>
            <input
              type="text"
              value={contact.phone}
              onChange={(e) => onContactChange('phone', e.target.value)}
              placeholder="Enter phone number"
              style={inputStyle}
            />
          </div>
        )}
      </div>

      {/* Email */}
      {(fieldFor('email')?.visible ?? true) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label style={labelStyle}>
            {fieldFor('email')?.label ?? 'Email'}
            {fieldFor('email')?.required && (
              <span style={{ color: 'var(--color-danger-text)' }}> *</span>
            )}
          </label>
          <input
            type="email"
            value={contact.email}
            onChange={(e) => onContactChange('email', e.target.value)}
            placeholder="Enter email address"
            style={inputStyle}
          />
        </div>
      )}
    </div>
  );
}

function EmergencyContactStepContent({
  collapsed,
  formState,
  onAddContact,
  onRemoveContact,
  onContactChange,
  ecFields,
}: {
  collapsed: boolean;
  formState: FormState;
  onAddContact: () => void;
  onRemoveContact: (index: number) => void;
  onContactChange: (index: number, field: keyof EmergencyContact, value: string) => void;
  ecFields: EffectivePatientField[];
}) {
  return (
    <div
      className={`clinic-canvas${collapsed ? ' clinic-canvas--collapsed' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        width: '100%',
      }}
    >
      {/* Section title */}
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-lg)',
          letterSpacing: '-0.44px',
          color: 'var(--text)',
        }}
      >
        Emergency Contact / Next of Kin
      </span>

      {/* Next of Kin cards */}
      {formState.emergencyContacts.map((contact, i) => (
        <NextOfKinCard
          key={i}
          index={i + 1}
          contact={contact}
          onContactChange={(field, value) => onContactChange(i, field, value)}
          onRemove={() => onRemoveContact(i)}
          canRemove={formState.emergencyContacts.length > 1}
          ecFields={ecFields}
        />
      ))}

      {/* Add Next of Kin button */}
      <button
        onClick={onAddContact}
        style={{
          width: '100%',
          height: 44,
          borderRadius: 'var(--input-radius)',
          border: '2px solid var(--border)',
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--sp-2)',
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-medium)',
          lineHeight: 'var(--line-height-base)',
          letterSpacing: '-0.15px',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="plus" /> Add Next of Kin
      </button>
    </div>
  );
}

// ─── Bottom Navigation ───

function BottomNav({
  onBack,
  onNext,
  backDisabled,
  nextDisabled,
  isLastStep,
}: {
  onBack: () => void;
  onNext: () => void;
  backDisabled: boolean;
  nextDisabled: boolean;
  isLastStep: boolean;
}) {
  return (
    <div
      style={{
        height: 'var(--clinic-bottom-nav-height)',
        padding: '21px var(--clinic-form-padding)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <Button
        variant="outline"
        size="md"
        leftIcon={<ChevronIcon direction="left" />}
        disabled={backDisabled}
        onClick={onBack}
        style={backDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        Back
      </Button>
      {isLastStep ? (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text-placeholder)',
          }}
        >
          Click <strong style={{ color: 'var(--text-muted)' }}>Create Patient</strong> above to
          finish
        </span>
      ) : (
        <Button
          variant="gradient"
          size="md"
          rightIcon={<ChevronIcon direction="right" />}
          disabled={nextDisabled}
          onClick={onNext}
          style={nextDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          Next
        </Button>
      )}
    </div>
  );
}

// ─── Intake Form Step Content (Step 5) ───

const fixtureQuestionnaire = publishedQuestionnaire as unknown as IntakeQuestionnaire;

function IntakeFormStepContent({
  collapsed,
  answers,
  onAnswersChange,
  questionnaire,
  banner,
  validationSchema,
}: {
  collapsed: boolean;
  answers: IntakeAnswers;
  onAnswersChange: (next: IntakeAnswers) => void;
  questionnaire: IntakeQuestionnaire;
  banner?: { type: 'info' | 'danger'; message: string } | null;
  validationSchema?: string | null;
}) {
  const missing = useMemo(
    () => getMissingRequired(questionnaire, answers),
    [questionnaire, answers],
  );

  return (
    <div
      className={`clinic-canvas${collapsed ? ' clinic-canvas--collapsed' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        width: '100%',
      }}
    >
      {/* Error summary banner */}
      {missing.length > 0 && (
        <div
          style={{
            background: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-border, #fecaca)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--color-danger-text)',
            }}
          >
            {missing.length} {missing.length === 1 ? 'issue' : 'issues'} found — Complete required
            fields to continue
          </span>
          <ul
            style={{
              margin: 0,
              paddingLeft: 'var(--sp-5)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            {missing.map((m) => (
              <li
                key={m.fieldKey}
                style={{
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-sm)',
                  color: 'var(--color-danger-text)',
                }}
              >
                {m.sectionTitle} → {m.questionLabel}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Banner: info or danger */}
      {banner && (
        <div
          style={{
            background:
              banner.type === 'danger'
                ? 'var(--color-danger-bg, #fef2f2)'
                : 'var(--color-info-bg, #eff6ff)',
            border: `1px solid ${banner.type === 'danger' ? 'var(--color-danger-border, #fecaca)' : 'var(--color-info-border, #bfdbfe)'}`,
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--sp-4)',
            fontSize: 'var(--font-size-sm)',
            color: banner.type === 'danger' ? 'var(--color-danger-text)' : 'var(--color-info)',
          }}
        >
          {banner.message}
        </div>
      )}

      {/* JSON-driven intake renderer */}
      <IntakeRenderer
        questionnaire={questionnaire}
        value={answers}
        onChange={onAnswersChange}
        errors={[]}
        showFieldErrors
        validationSchema={validationSchema}
      />
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        padding: 'var(--sp-6) 0 var(--sp-3)',
        borderBottom: '2px solid var(--border)',
        marginBottom: 'var(--sp-4)',
      }}
    >
      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-lg)',
          letterSpacing: '-0.44px',
          color: 'var(--text)',
          margin: 0,
        }}
      >
        {title}
      </h2>
    </div>
  );
}

// ─── Patient Created Success Drawer ───

function PatientCreatedDrawer({
  open,
  onOpenChange,
  patientName,
  patientNumber,
  onGoToDetail,
  onCreateAnother,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientName: string;
  patientNumber: string;
  onGoToDetail: () => void;
  onCreateAnother: () => void;
}) {
  const brandTokens = useBrandTokens();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Patient Created"
      maxWidth={420}
      portalStyle={brandTokens as import('react').CSSProperties}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3, 12px)' }}>
          <Button variant="gradient" size="lg" onClick={onGoToDetail} style={{ width: '100%' }}>
            <NavIcon name="user" /> Go to Patient Detail
          </Button>
          <Button variant="outline" size="lg" onClick={onCreateAnother} style={{ width: '100%' }}>
            <NavIcon name="plus" /> Create Another Patient
          </Button>
          <button
            disabled
            style={{
              width: '100%',
              height: 42,
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border, #e5e7eb)',
              background: 'var(--bg-surface-2, #f9fafb)',
              fontSize: 'var(--font-size-base, 14px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              lineHeight: 'var(--line-height-base, 20px)',
              color: 'var(--text-placeholder, #9ca3af)',
              cursor: 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--sp-2, 8px)',
              fontFamily: 'inherit',
              opacity: 0.6,
            }}
          >
            <NavIcon name="list" /> Add Patient to Queue
            <span
              style={{
                fontSize: 'var(--font-size-sm, 12px)',
                color: 'var(--text-placeholder, #9ca3af)',
              }}
            >
              — Coming soon
            </span>
          </button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Success icon + message */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--sp-4, 16px)',
            padding: 'var(--sp-6, 24px) 0',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 'var(--radius-full, 9999px)',
              background: 'var(--color-success-bg, #dcfce7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-success-text, #166534)',
            }}
          >
            <NavIcon name="check-circle" />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: 'var(--font-size-lg, 16px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                lineHeight: 'var(--line-height-lg, 24px)',
                letterSpacing: '-0.31px',
                color: 'var(--text, #111827)',
              }}
            >
              {patientName}
            </div>
            {patientNumber && (
              <div
                style={{
                  fontSize: 'var(--font-size-base, 14px)',
                  lineHeight: 'var(--line-height-base, 20px)',
                  color: 'var(--text-muted, #6b7280)',
                  marginTop: 4,
                }}
              >
                Patient #{patientNumber}
              </div>
            )}
          </div>
        </div>

        {/* What's next section */}
        <div
          style={{
            background: 'var(--color-info-bg, #eff6ff)',
            border: '1px solid var(--color-info-border, #bfdbfe)',
            borderRadius: 'var(--radius-md, 10px)',
            padding: '12px 17px',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-3, 12px)' }}>
            <div style={{ color: 'var(--color-info, #3b82f6)', flexShrink: 0, marginTop: 1 }}>
              <NavIcon name="info" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm, 12px)',
                lineHeight: '19.5px',
                color: '#1e40af',
              }}
            >
              Patient record has been saved. Choose an action below to continue.
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Page ───

export function CreatePatientPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswers>({});
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());
  const [isSavingPatient, setIsSavingPatient] = useState(false);
  const [savePatientError, setSavePatientError] = useState<string | null>(null);
  const [createdPatientId, setCreatedPatientId] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPatientName, setCreatedPatientName] = useState('');
  const [createdPatientNumber, setCreatedPatientNumber] = useState('');
  const { collapsed } = useOutletContext<{ collapsed: boolean }>();
  const { db } = usePowerSyncDb();
  const navigate = useNavigate();
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // ── Layout mode from app_config ──
  const { data: appConfigRows } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const isSinglePage = useMemo(
    () => getPatientFormLayoutMode(appConfigRows?.[0]) === 'single_page',
    [appConfigRows],
  );

  // Track which section is in view for single-page stepper highlight
  useEffect(() => {
    if (!isSinglePage) return;
    const root = scrollContainerRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const step = Number((entry.target as HTMLElement).dataset.step);
            if (step) setCurrentStep(step);
          }
        }
      },
      { root, rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );

    for (const [step, el] of Object.entries(sectionRefs.current)) {
      if (el) {
        el.dataset.step = step;
        observer.observe(el);
      }
    }

    return () => observer.disconnect();
  }, [isSinglePage]);

  // ── Step 1: effective fields from PowerSync overrides ──
  const { data: dbOverrides = [] } = usePowerSyncQuery<PatientFieldDefinitionRow>(
    qPatientFieldDefinitionOverrides(),
  );

  const effectiveFields = useMemo(
    () => buildPatientFormSettingsVM(PATIENT_CORE_FIELDS, dbOverrides),
    [dbOverrides],
  );

  // Per-step validation schemas (merged from per-field validation_override)
  const step1ValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Personal Info')),
    [effectiveFields],
  );
  const step2ValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Contact')),
    [effectiveFields],
  );
  const step4ValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Medication')),
    [effectiveFields],
  );

  // PRD.232 — ID Type options come from Clinic Settings → regional
  // settings (`app_config.extra_json.regional_settings.id_types`). The
  // helper auto-preserves an existing form value that's no longer in the
  // enabled list so users editing an older record don't lose their
  // selection. Per-field `validation_override.idTypeOptions` (the legacy
  // PatientFormSettings path) is intentionally no longer consulted.
  const enabledIdTypeLabels = useEnabledIdTypeLabels(formState.idType);
  const idTypeOpts = useMemo(() => enabledIdTypeLabels.map(opt), [enabledIdTypeLabels]);

  const step1Options = useMemo<FieldOptionsMap>(
    () => ({ ...STEP1_OPTIONS, idType: idTypeOpts }),
    [idTypeOpts],
  );

  const step1Questionnaire = useMemo(
    () => buildStepQuestionnaire(effectiveFields, 'Personal Info', step1Options),
    [effectiveFields, step1Options],
  );

  // Step 1 answers (IntakeAnswers format), seeded from initial FormState
  const [step1Answers, setStep1Answers] = useState<IntakeAnswers>(() => {
    const answers: IntakeAnswers = {};
    for (const field of PATIENT_CORE_FIELDS) {
      if (field.step !== 'Personal Info') continue;
      const val = (INITIAL_FORM_STATE as unknown as Record<string, unknown>)[field.field_key];
      if (typeof val === 'string' && val) answers[field.field_key] = val;
    }
    return answers;
  });

  // Auto-generate patient number
  const [hasUserEditedPatientId, setHasUserEditedPatientId] = useState(false);

  const fetchNextPatientNumber = useCallback(async () => {
    if (!db) return null;
    const rows = await db.getAll<{ max_num: number | null }>(
      `SELECT MAX(CAST(patient_number AS INTEGER)) AS max_num FROM patients WHERE patient_number != '' AND patient_number NOT GLOB '*[^0-9]*'`,
    );
    return String((rows[0]?.max_num ?? 0) + 1);
  }, [db]);

  // Auto-fill on mount
  useEffect(() => {
    if (!db || hasUserEditedPatientId) return;
    (async () => {
      const next = await fetchNextPatientNumber();
      if (!next) return;
      setStep1Answers((prev) => {
        if (prev.patientId && prev.patientId !== '') return prev;
        return { ...prev, patientId: next };
      });
      setFormState((prev) => {
        if (prev.patientId && prev.patientId !== '') return prev;
        return { ...prev, patientId: next };
      });
    })();
  }, [db, hasUserEditedPatientId, fetchNextPatientNumber]);

  const handleRefreshPatientNumber = useCallback(async () => {
    const next = await fetchNextPatientNumber();
    if (!next) return;
    setStep1Answers((prev) => ({ ...prev, patientId: next }));
    setFormState((prev) => ({ ...prev, patientId: next }));
    setHasUserEditedPatientId(false);
  }, [fetchNextPatientNumber]);

  const handleClearPatientNumber = useCallback(() => {
    setStep1Answers((prev) => ({ ...prev, patientId: '' }));
    setFormState((prev) => ({ ...prev, patientId: '' }));
    setHasUserEditedPatientId(true);
  }, []);

  const step1FieldRenderers = useMemo<Record<string, FieldRenderer>>(
    () => ({
      patientId: ({ question, value, onChange, readOnly, hasError }) => {
        const strVal = typeof value === 'string' ? value : '';
        const borderColor = hasError ? 'var(--color-danger-text)' : 'var(--border)';
        const groupBtnStyle: React.CSSProperties = {
          width: 38,
          height: 'var(--input-height, 42px)',
          border: `1px solid ${borderColor}`,
          borderLeft: 'none',
          background: 'var(--bg-surface-2)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          padding: 0,
          flexShrink: 0,
        };
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              value={strVal}
              onChange={(e) => onChange(question.fieldKey, e.target.value)}
              readOnly={readOnly}
              placeholder="Auto-generated"
              style={{
                flex: 1,
                height: 'var(--input-height, 42px)',
                padding: '0 var(--input-padding-x, 16px)',
                borderRadius: 'var(--input-radius, 10px) 0 0 var(--input-radius, 10px)',
                border: `1px solid ${borderColor}`,
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--input-text)',
                fontFamily: 'inherit',
                boxSizing: 'border-box' as const,
              }}
            />
            {strVal && (
              <button
                type="button"
                onClick={() => {
                  onChange(question.fieldKey, '');
                  handleClearPatientNumber();
                }}
                title="Clear"
                style={{ ...groupBtnStyle }}
              >
                <NavIcon name="x" />
              </button>
            )}
            <button
              type="button"
              onClick={handleRefreshPatientNumber}
              title="Fetch latest number"
              style={{
                ...groupBtnStyle,
                borderRadius: `0 var(--input-radius, 10px) var(--input-radius, 10px) 0`,
              }}
            >
              <NavIcon name="refresh-cw" />
            </button>
          </div>
        );
      },
    }),
    [handleClearPatientNumber, handleRefreshPatientNumber],
  );

  const handleStep1Change = useCallback((next: IntakeAnswers) => {
    setStep1Answers((prev) => {
      if (next.patientId !== prev.patientId) {
        setHasUserEditedPatientId(true);
      }
      return next;
    });
    // Sync back to FormState for cross-step compatibility
    setFormState((prev) => {
      const updated = { ...prev };
      for (const [key, val] of Object.entries(next)) {
        if (key in prev && typeof val === 'string') {
          (updated as Record<string, unknown>)[key] = val;
        }
      }
      return updated as FormState;
    });
  }, []);

  // ── Step 2: Contact ─
  const step2Questionnaire = useMemo(
    () => buildStepQuestionnaire(effectiveFields, 'Contact'),
    [effectiveFields],
  );

  // Country picker config from mobileCountryCode field's validation_override
  const countryPickerConfig = useMemo(() => {
    const mccField = effectiveFields.find((f) => f.field_key === 'mobileCountryCode');
    return getMobileCountryPickerConfig(mccField?.validation_override);
  }, [effectiveFields]);

  // Show every country in the picker; configured `defaultCountry` is still the pre-selected one.
  const countryOptions = useMemo(() => buildCountryOptions(ALL_COUNTRIES), []);

  // Field renderer override for mobileCountryCode → CountryPicker widget
  // State stores ISO2 (e.g. 'PH'); converted to dial code at save time.
  const step2FieldRenderers = useMemo<Record<string, FieldRenderer>>(
    () => ({
      mobileCountryCode: ({ value, onChange, readOnly }) => {
        const iso2 = typeof value === 'string' ? value : countryPickerConfig.defaultCountry;
        return (
          <CountryPicker
            options={countryOptions}
            valueIso2={iso2}
            onChangeIso2={(newIso2) => onChange('mobileCountryCode', newIso2)}
            disabled={readOnly}
          />
        );
      },
    }),
    [countryOptions, countryPickerConfig.defaultCountry],
  );

  const [step2Answers, setStep2Answers] = useState<IntakeAnswers>(() => {
    // Seed default country as ISO2
    return { mobileCountryCode: countryPickerConfig.defaultCountry };
  });

  const handleStep2Change = useCallback((next: IntakeAnswers) => {
    setStep2Answers(next);
    setFormState((prev) => {
      const updated = { ...prev };
      for (const [key, val] of Object.entries(next)) {
        if (key in prev && typeof val === 'string') {
          (updated as unknown as Record<string, unknown>)[key] = val;
        }
      }
      return updated as FormState;
    });
  }, []);

  // ── Step 4: Medication (multi-section) ──
  const step4Questionnaire = useMemo(
    () =>
      buildMultiSectionQuestionnaire(effectiveFields, 'Medication', STEP4_SECTIONS, STEP4_OPTIONS),
    [effectiveFields],
  );

  const [step4Answers, setStep4Answers] = useState<IntakeAnswers>({});

  const handleStep4Change = useCallback((next: IntakeAnswers) => {
    setStep4Answers(next);
    setFormState((prev) => {
      const updated = { ...prev };
      for (const [key, val] of Object.entries(next)) {
        if (key in prev && typeof val === 'string') {
          (updated as unknown as Record<string, unknown>)[key] = val;
        }
      }
      return updated as FormState;
    });
  }, []);

  // ── Step 5: Intake Form from published definition ──
  const { data: publishedRows = [] } = usePowerSyncQuery<IntakeFormDefinitionRow>(
    qLatestPublishedIntakeFormDefinition(),
  );

  const { step5Questionnaire, step5Banner, step5ValidationSchema } = useMemo(() => {
    if (publishedRows.length === 0) {
      return {
        step5Questionnaire: fixtureQuestionnaire,
        step5Banner: {
          type: 'info' as const,
          message: 'No published intake form found. Publish one in Settings \u2192 Intake Forms.',
        },
        step5ValidationSchema: null,
      };
    }
    const row = publishedRows[0];
    // Try to parse the schema TEXT column
    let parsed: IntakeQuestionnaire | null = null;
    if (row.schema != null && row.schema !== '') {
      try {
        parsed = JSON.parse(row.schema) as IntakeQuestionnaire;
      } catch {
        // invalid JSON
      }
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sections)) {
      return {
        step5Questionnaire: fixtureQuestionnaire,
        step5Banner: {
          type: 'danger' as const,
          message:
            'Published intake form schema is invalid. Please fix in Settings \u2192 Intake Forms.',
        },
        step5ValidationSchema: null,
      };
    }
    return {
      step5Questionnaire: parsed,
      step5Banner: null,
      step5ValidationSchema: row.validation_schema ?? null,
    };
  }, [publishedRows]);

  // ── Validation ──
  const stepErrors = useMemo(() => {
    if (currentStep === 1) {
      return getMissingRequired(step1Questionnaire, step1Answers).map((m) => m.fieldKey);
    }
    if (currentStep === 2) {
      return getMissingRequired(step2Questionnaire, step2Answers).map((m) => m.fieldKey);
    }
    if (currentStep === 4) {
      return getMissingRequired(step4Questionnaire, step4Answers).map((m) => m.fieldKey);
    }
    return getStepErrors(currentStep, formState);
  }, [
    currentStep,
    formState,
    step1Questionnaire,
    step1Answers,
    step2Questionnaire,
    step2Answers,
    step4Questionnaire,
    step4Answers,
  ]);

  const isLastStep = currentStep === STEPS.length;

  const missingRequired = useMemo(
    () => (isLastStep ? getMissingRequired(step5Questionnaire, intakeAnswers) : []),
    [isLastStep, step5Questionnaire, intakeAnswers],
  );

  const showErrors = attemptedSteps.has(currentStep);

  // In single-page mode, compute which steps are valid for stepper checkmarks
  const validSteps = useMemo(() => {
    if (!isSinglePage) return new Set<number>();
    const valid = new Set<number>();
    if (
      getMissingRequired(step1Questionnaire, step1Answers).length === 0 &&
      getStepErrors(1, formState).length === 0 &&
      !hasAdvancedErrors(step1Questionnaire, step1ValidationSchema, step1Answers)
    )
      valid.add(1);
    if (
      getMissingRequired(step2Questionnaire, step2Answers).length === 0 &&
      getStepErrors(2, formState).length === 0 &&
      !hasAdvancedErrors(step2Questionnaire, step2ValidationSchema, step2Answers)
    )
      valid.add(2);
    if (getStepErrors(3, formState).length === 0) valid.add(3);
    if (
      getMissingRequired(step4Questionnaire, step4Answers).length === 0 &&
      getStepErrors(4, formState).length === 0 &&
      !hasAdvancedErrors(step4Questionnaire, step4ValidationSchema, step4Answers)
    )
      valid.add(4);
    if (getMissingRequired(step5Questionnaire, intakeAnswers).length === 0) valid.add(5);
    return valid;
  }, [
    isSinglePage,
    formState,
    step1Questionnaire,
    step1ValidationSchema,
    step1Answers,
    step2Questionnaire,
    step2ValidationSchema,
    step2Answers,
    step4Questionnaire,
    step4ValidationSchema,
    step4Answers,
    step5Questionnaire,
    intakeAnswers,
  ]);

  const handleContactChange = useCallback(
    (index: number, field: keyof EmergencyContact, value: string) => {
      setFormState((prev) => {
        const updated = prev.emergencyContacts.map((c, i) =>
          i === index ? { ...c, [field]: value } : c,
        );
        return { ...prev, emergencyContacts: updated };
      });
    },
    [],
  );

  const handleAddContact = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      emergencyContacts: [...prev.emergencyContacts, { ...EMPTY_CONTACT }],
    }));
  }, []);

  const handleRemoveContact = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index),
    }));
  }, []);

  const handleBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  const handleNext = () => {
    setAttemptedSteps((prev) => new Set(prev).add(currentStep));
    // Steps 1, 2, 4 use settings-driven validation
    if (currentStep === 1) {
      if (getMissingRequired(step1Questionnaire, step1Answers).length > 0) return;
      if (hasAdvancedErrors(step1Questionnaire, step1ValidationSchema, step1Answers)) return;
    } else if (currentStep === 2) {
      if (getMissingRequired(step2Questionnaire, step2Answers).length > 0) return;
      if (hasAdvancedErrors(step2Questionnaire, step2ValidationSchema, step2Answers)) return;
    } else if (currentStep === 4) {
      if (getMissingRequired(step4Questionnaire, step4Answers).length > 0) return;
      if (hasAdvancedErrors(step4Questionnaire, step4ValidationSchema, step4Answers)) return;
    } else {
      const errors = getStepErrors(currentStep, formState);
      if (errors.length > 0) return;
    }
    const next = currentStep + 1;
    setCurrentStep(next);
    setMaxStepReached((prev) => Math.max(prev, next));
  };

  const handleStepClick = (step: number) => {
    if (isSinglePage) {
      sectionRefs.current[step]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (step <= maxStepReached) {
      setCurrentStep(step);
    }
  };

  // ── Save patient + intake to PowerSync local SQLite ──
  const handleCreatePatient = useCallback(async () => {
    if (!db || isSavingPatient || createdPatientId) return;

    // Mark all steps as attempted so inline errors render
    setAttemptedSteps(new Set([1, 2, 3, 4, 5]));

    // Validation gates
    if (!formState.patientId.trim() || !formState.patientName.trim()) {
      setSavePatientError('Patient ID and Patient Name are required.');
      return;
    }
    if (missingRequired.length > 0) {
      setSavePatientError('Please complete all required intake fields.');
      return;
    }
    if (
      hasAdvancedErrors(step1Questionnaire, step1ValidationSchema, step1Answers) ||
      hasAdvancedErrors(step2Questionnaire, step2ValidationSchema, step2Answers) ||
      hasAdvancedErrors(step4Questionnaire, step4ValidationSchema, step4Answers)
    ) {
      setSavePatientError('Please fix validation errors before creating the patient.');
      return;
    }

    // Check for duplicate patient number locally
    const dupeCheck = await db.getAll<{ id: string }>(
      `SELECT id FROM patients WHERE patient_number = ? LIMIT 1`,
      [formState.patientId.trim()],
    );
    if (dupeCheck.length > 0) {
      setSavePatientError('A patient with this ID number already exists.');
      return;
    }

    // Require a published intake form definition
    const publishedDef = publishedRows[0];
    if (!publishedDef) {
      setSavePatientError(
        'No published intake form found. Publish one in Settings \u2192 Intake Forms.',
      );
      return;
    }

    setIsSavingPatient(true);
    setSavePatientError(null);

    try {
      const genId = () =>
        crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      // 1) Insert patients row
      const patientId = genId();
      const cols = buildPatientInsertFromForm(patientId, formState);
      const keys = Object.keys(cols);
      const placeholders = keys.map(() => '?').join(', ');
      const values = keys.map((k) => cols[k]);

      await db.execute(
        `INSERT INTO patients (${keys.join(', ')}) VALUES (${placeholders})`,
        values,
      );
      console.log('[CreatePatient] Patient saved:', patientId);

      // 2) Insert patient_intake_forms (links patient to published definition)
      const patientIntakeFormId = genId();
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_intake_forms (id, patient_id, intake_form_definition_id, status, completed_at) VALUES (?, ?, ?, ?, ?)`,
        [patientIntakeFormId, patientId, publishedDef.id, 'completed', now],
      );
      console.log('[CreatePatient] Intake form link saved:', patientIntakeFormId);

      // 3) Insert intake_form_responses (answers payload)
      const responseId = genId();
      await db.execute(
        `INSERT INTO intake_form_responses (id, patient_intake_form_id, answers) VALUES (?, ?, ?)`,
        [responseId, patientIntakeFormId, JSON.stringify(intakeAnswers)],
      );
      console.log('[CreatePatient] Intake responses saved:', responseId);

      // 4) Insert patient_contacts
      const contactId = genId();
      const contactCols = buildPatientContactsInsertFromForm(contactId, patientId, formState);
      const contactKeys = Object.keys(contactCols);
      const contactPlaceholders = contactKeys.map(() => '?').join(', ');
      const contactValues = contactKeys.map((k) => contactCols[k]);
      await db.execute(
        `INSERT INTO patient_contacts (${contactKeys.join(', ')}) VALUES (${contactPlaceholders})`,
        contactValues,
      );
      console.log('[CreatePatient] Contact saved:', contactId);

      // 5) Insert patient_allergies (0–2 rows)
      const allergyRows = buildPatientAllergyInsertsFromForm(formState, patientId, genId);
      for (const row of allergyRows) {
        const aKeys = Object.keys(row);
        const aPlaceholders = aKeys.map(() => '?').join(', ');
        const aValues = aKeys.map((k) => row[k]);
        await db.execute(
          `INSERT INTO patient_allergies (${aKeys.join(', ')}) VALUES (${aPlaceholders})`,
          aValues,
        );
        console.log('[CreatePatient] Allergy saved:', row.id, row.allergy_type);
      }

      // 6) Insert patient_identifiers (if both idType and idNumber provided)
      const idType = formState.idType.trim();
      const idValue = formState.idNumber.trim();
      if (idType && idValue) {
        const identifierId = genId();
        await db.execute(
          `INSERT INTO patient_identifiers (id, patient_id, id_type, id_value) VALUES (?, ?, ?, ?)`,
          [identifierId, patientId, idType, idValue],
        );
        console.log('[CreatePatient] Identifier saved:', identifierId, idType);
      }

      // 7) Insert patient_emergency_contacts
      for (let i = 0; i < formState.emergencyContacts.length; i++) {
        const ec = formState.emergencyContacts[i];
        const ecName = ec.name.trim();
        const ecRelationship = ec.relationship.trim();
        const ecPhone = ec.phone.trim();
        const ecEmail = ec.email.trim();
        // Skip entries with no meaningful data
        if (!ecName && !ecRelationship && !ecPhone && !ecEmail) continue;
        const ecId = genId();
        await db.execute(
          `INSERT INTO patient_emergency_contacts (id, patient_id, name, relationship, phone, email) VALUES (?, ?, ?, ?, ?, ?)`,
          [
            ecId,
            patientId,
            ecName || null,
            ecRelationship || null,
            ecPhone || null,
            ecEmail || null,
          ],
        );
        console.log('[CreatePatient] Emergency contact saved:', ecId);
      }

      setCreatedPatientId(patientId);
      setCreatedPatientName(formState.patientName.trim());
      setCreatedPatientNumber(formState.patientId.trim());
      setShowSuccessModal(true);
    } catch (err) {
      console.error('[CreatePatient] Save failed:', err);
      setSavePatientError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSavingPatient(false);
    }
  }, [
    db,
    isSavingPatient,
    createdPatientId,
    formState,
    missingRequired,
    publishedRows,
    intakeAnswers,
    step1Questionnaire,
    step1ValidationSchema,
    step1Answers,
    step2Questionnaire,
    step2ValidationSchema,
    step2Answers,
    step4Questionnaire,
    step4ValidationSchema,
    step4Answers,
  ]);

  // ── Success modal actions ──

  const handleGoToDetail = useCallback(() => {
    if (!createdPatientId) return;
    setShowSuccessModal(false);
    navigate(`/patient/${createdPatientId}`);
  }, [createdPatientId, navigate]);

  const handleCreateAnother = useCallback(() => {
    setShowSuccessModal(false);
    setCreatedPatientId(null);
    setCreatedPatientName('');
    setCreatedPatientNumber('');
    setSavePatientError(null);
    setFormState(INITIAL_FORM_STATE);
    setIntakeAnswers({});
    setAttemptedSteps(new Set());
    setCurrentStep(1);
    setMaxStepReached(1);
    // Reset per-step answers to initial
    const initialStep1: IntakeAnswers = {};
    for (const field of PATIENT_CORE_FIELDS) {
      if (field.step !== 'Personal Info') continue;
      const val = (INITIAL_FORM_STATE as unknown as Record<string, unknown>)[field.field_key];
      if (typeof val === 'string' && val) initialStep1[field.field_key] = val;
    }
    setStep1Answers(initialStep1);
    setStep2Answers({});
    setStep4Answers({});
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title={isSinglePage || isLastStep ? 'New Patient Intake' : undefined}
        subtitle={
          isSinglePage || isLastStep ? 'Comprehensive intake form for new patients' : undefined
        }
        versionPill={
          isSinglePage || isLastStep
            ? ((publishedRows[0]?.version as number | undefined) ?? undefined)
            : undefined
        }
        createDisabled={
          isSinglePage
            ? validSteps.size < STEPS.length
            : isLastStep
              ? missingRequired.length > 0
              : true
        }
        onCreatePatient={handleCreatePatient}
        saving={isSavingPatient}
        saveError={savePatientError}
        createdPatientId={createdPatientId}
      />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <FormStepper
          currentStep={currentStep}
          maxStepReached={isSinglePage ? STEPS.length : maxStepReached}
          onStepClick={handleStepClick}
          singlePage={isSinglePage}
          validSteps={validSteps}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Scrollable form area */}
          <div
            ref={scrollContainerRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 'var(--clinic-form-padding)',
            }}
          >
            {(isSinglePage || currentStep === 1) && (
              <div
                ref={(el) => {
                  sectionRefs.current[1] = el;
                }}
              >
                {isSinglePage && <SectionHeader title="1. Personal Info" />}
                <div
                  className={`clinic-canvas${collapsed ? ' clinic-canvas--collapsed' : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-5)',
                    width: '100%',
                  }}
                >
                  <IntakeRenderer
                    questionnaire={step1Questionnaire}
                    value={step1Answers}
                    onChange={handleStep1Change}
                    errors={[]}
                    showFieldErrors={showErrors}
                    validationSchema={step1ValidationSchema}
                    fieldRenderers={step1FieldRenderers}
                  />
                </div>
              </div>
            )}
            {(isSinglePage || currentStep === 2) && (
              <div
                ref={(el) => {
                  sectionRefs.current[2] = el;
                }}
              >
                {isSinglePage && <SectionHeader title="2. Contact" />}
                <div
                  className={`clinic-canvas${collapsed ? ' clinic-canvas--collapsed' : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-5)',
                    width: '100%',
                  }}
                >
                  <IntakeRenderer
                    questionnaire={step2Questionnaire}
                    value={step2Answers}
                    onChange={handleStep2Change}
                    fieldRenderers={step2FieldRenderers}
                    errors={[]}
                    showFieldErrors={showErrors}
                    validationSchema={step2ValidationSchema}
                  />
                </div>
              </div>
            )}
            {(isSinglePage || currentStep === 3) && (
              <div
                ref={(el) => {
                  sectionRefs.current[3] = el;
                }}
              >
                {isSinglePage && <SectionHeader title="3. Emergency Contact" />}
                <EmergencyContactStepContent
                  collapsed={collapsed}
                  formState={formState}
                  onAddContact={handleAddContact}
                  onRemoveContact={handleRemoveContact}
                  onContactChange={handleContactChange}
                  ecFields={effectiveFields.filter((f) => f.step === 'Emergency Contact')}
                />
              </div>
            )}
            {(isSinglePage || currentStep === 4) && (
              <div
                ref={(el) => {
                  sectionRefs.current[4] = el;
                }}
              >
                {isSinglePage && <SectionHeader title="4. Medication" />}
                <div
                  className={`clinic-canvas${collapsed ? ' clinic-canvas--collapsed' : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-5)',
                    width: '100%',
                  }}
                >
                  <IntakeRenderer
                    questionnaire={step4Questionnaire}
                    value={step4Answers}
                    onChange={handleStep4Change}
                    errors={[]}
                    showFieldErrors={showErrors}
                    validationSchema={step4ValidationSchema}
                  />
                </div>
              </div>
            )}
            {(isSinglePage || currentStep === 5) && (
              <div
                ref={(el) => {
                  sectionRefs.current[5] = el;
                }}
              >
                {isSinglePage && <SectionHeader title="5. Intake Form" />}
                <IntakeFormStepContent
                  collapsed={collapsed}
                  answers={intakeAnswers}
                  onAnswersChange={setIntakeAnswers}
                  questionnaire={step5Questionnaire}
                  banner={step5Banner}
                  validationSchema={step5ValidationSchema}
                />
              </div>
            )}
          </div>

          {!isSinglePage && (
            <BottomNav
              onBack={handleBack}
              onNext={handleNext}
              backDisabled={currentStep === 1}
              nextDisabled={stepErrors.length > 0}
              isLastStep={isLastStep}
            />
          )}
        </div>
      </div>

      <PatientCreatedDrawer
        open={showSuccessModal}
        onOpenChange={setShowSuccessModal}
        patientName={createdPatientName}
        patientNumber={createdPatientNumber}
        onGoToDetail={handleGoToDetail}
        onCreateAnother={handleCreateAnother}
      />
    </div>
  );
}
